import { Link, useLocation } from 'react-router-dom';
import { Home, Trophy, ListOrdered, Users, BookOpen, Settings, Moon, Sun } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLadder } from '../contexts/LadderContext';
import { Button } from './ui/button';

// Phidél branding component - now used only in footer
const PhidelBadge = () => (
  <span className="phidel-badge">
    Powered by <span className="phidel-name">Phidél</span>
  </span>
);

// League subtitle component for header
const LeagueSubtitle = ({ subtitle }) => {
  if (!subtitle) return null;
  return (
    <span className="league-subtitle">
      {subtitle}
    </span>
  );
};

// Mobile Bottom Navigation Component - Now with 5 items including Rules
export const MobileBottomNav = () => {
  const { t } = useLanguage();
  const location = useLocation();

  const navItems = [
    { path: '/', label: t('home'), icon: Home },
    { path: '/leaderboard', label: t('leaderboard'), icon: Trophy },
    { path: '/scores', label: t('scores'), icon: ListOrdered },
    { path: '/partners', label: t('partners'), icon: Users },
    { path: '/rules', label: t('rules'), icon: BookOpen },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bottom-nav" data-testid="mobile-bottom-nav">
      {navItems.map(({ path, label, icon: Icon }) => (
        <Link
          key={path}
          to={path}
          data-testid={`bottom-nav-${path.replace('/', '') || 'home'}`}
          className={`bottom-nav-item ${isActive(path) ? 'active' : ''}`}
        >
          <Icon className="h-5 w-5" />
          <span>{label}</span>
        </Link>
      ))}
    </nav>
  );
};

export const PublicHeader = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { ladder } = useLadder();
  const location = useLocation();

  const navItems = [
    { path: '/', label: t('home'), icon: Home },
    { path: '/leaderboard', label: t('leaderboard'), icon: Trophy },
    { path: '/scores', label: t('scores'), icon: ListOrdered },
    { path: '/partners', label: t('partners'), icon: Users },
    { path: '/rules', label: t('rules'), icon: BookOpen },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="header-main sticky top-0 z-50 bg-card/95 backdrop-blur-sm border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo / Club Name - Hidden in mobile landscape */}
          <Link to="/" className="flex items-center gap-3 header-logo" data-testid="header-logo">
            {ladder?.club_logo ? (
              <img 
                src={ladder.club_logo} 
                alt="Club Logo" 
                className="h-10 w-10 object-contain rounded-lg"
              />
            ) : (
              <div className="h-10 w-10 bg-primary rounded-lg flex items-center justify-center">
                <Trophy className="h-5 w-5 text-primary-foreground" />
              </div>
            )}
            <div>
              <h1 className="font-heading font-bold text-foreground text-lg leading-tight">
                {ladder?.name || 'Padel Ladder'}
              </h1>
              <LeagueSubtitle subtitle={ladder?.subtitle} />
            </div>
          </Link>

          {/* Desktop Navigation - Hidden in mobile */}
          <nav className="hidden md:flex items-center gap-1 header-desktop-nav">
            {navItems.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                data-testid={`nav-${path.replace('/', '') || 'home'}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </Link>
            ))}
          </nav>

          {/* Right side: Theme + Language + Admin */}
          <div className="flex items-center gap-2">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="theme-toggle"
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </button>

            {/* Language Toggle - EN first, then EL */}
            <div className="lang-toggle" data-testid="language-toggle">
              <button
                onClick={() => language !== 'en' && toggleLanguage()}
                className={language === 'en' ? 'active' : ''}
              >
                EN
              </button>
              <button
                onClick={() => language !== 'el' && toggleLanguage()}
                className={language === 'el' ? 'active' : ''}
              >
                EL
              </button>
            </div>

            {/* Admin Link - Desktop only, hidden on all mobile views */}
            <Link to="/admin" data-testid="admin-link" className="hidden lg:block">
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                {t('admin')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export { PhidelBadge };
